import { supabase } from '../lib/supabase';

/**
 * Admin Authentication Service
 * Handles authentication for admin_accounts table (separate from regular user auth)
 */

export const adminAuthService = {
  /**
   * Sign in admin using admin_accounts table
   * @param {string} email - Admin email
   * @param {string} password - Admin password
   * @returns {Promise<Object>} Admin account data
   */
  async signIn(email, password) {
    try {
      // Call database function to verify admin credentials
      const { data, error } = await supabase?.rpc('verify_admin_credentials', {
          admin_email: email,
          admin_password: password
        });

      if (error) {
        console.error('Admin sign in error:', error);
        throw new Error('Invalid credentials or account not found');
      }

      if (!data || data?.length === 0) {
        throw new Error('Invalid email or password');
      }

      const adminAccount = data?.[0];

      // Update last login timestamp
      await supabase?.rpc('update_admin_last_login', {
        admin_id: adminAccount?.id
      });

      // Store admin session in localStorage
      const adminSession = {
        id: adminAccount?.id,
        email: adminAccount?.email,
        fullName: adminAccount?.full_name,
        role: adminAccount?.role,
        isActive: adminAccount?.is_active,
        loginAt: new Date()?.toISOString()
      };

      localStorage.setItem('admin_session', JSON.stringify(adminSession));

      return adminAccount;
    } catch (error) {
      console.error('Admin authentication error:', error);
      throw error;
    }
  },

  /**
   * Sign out admin
   */
  async signOut() {
    try {
      // Clear admin session from localStorage
      localStorage.removeItem('admin_session');
      return { success: true };
    } catch (error) {
      console.error('Admin sign out error:', error);
      throw error;
    }
  },

  /**
   * Get current admin session
   * @returns {Object|null} Admin session data or null
   */
  getCurrentSession() {
    try {
      const sessionData = localStorage.getItem('admin_session');
      if (!sessionData) return null;

      const session = JSON.parse(sessionData);
      
      // Check if session is still valid (24 hours)
      const loginTime = new Date(session?.loginAt);
      const now = new Date();
      const hoursSinceLogin = (now - loginTime) / (1000 * 60 * 60);

      if (hoursSinceLogin > 24) {
        // Session expired
        localStorage.removeItem('admin_session');
        return null;
      }

      return session;
    } catch (error) {
      console.error('Error getting admin session:', error);
      return null;
    }
  },

  /**
   * Check if admin is authenticated
   * @returns {boolean}
   */
  isAuthenticated() {
    const session = this.getCurrentSession();
    return session !== null && session?.isActive === true;
  },

  /**
   * Verify admin email exists
   * @param {string} email - Email to check
   * @returns {Promise<boolean>}
   */
  async verifyAdminEmail(email) {
    try {
      const { data, error } = await supabase?.rpc('is_admin_account', {
          check_email: email
        });

      if (error) throw error;
      return data === true;
    } catch (error) {
      console.error('Error verifying admin email:', error);
      return false;
    }
  }
};
